import { useCallback } from 'react';

import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { DbFolder } from '~src/types/db';
import { getParentsIds } from '~src/utils/tree';

/**
 * @param parentId - item's parent id (will be last path element)
 * @returns item's location path
 */
type UseGetItemPathReturn = (parentId: DbFolder['parent_id']) => string;

/**
 * Made it into a hook, to use `foldersQuery`
 * @returns `getItemPath` function */
export const useGetItemPath = (): UseGetItemPathReturn => {
  const foldersQuery = useFolders(
    {
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );

  const getItemPath = useCallback<UseGetItemPathReturn>(
    (folderId) => {
      if (!folderId) return '/'; // is on root
      if (!foldersQuery.data) return '...'; // foldersQuery not ready

      const parentIds = getParentsIds(foldersQuery.data, String(folderId));
      parentIds.push(String(folderId)); // insert parent id at the end

      // get folder names matching parentIds
      const parentNames = parentIds
        .map((strId) => {
          const id = Number(strId);

          const dbFolder = foldersQuery.data?.find((v) => v.id === id);
          if (!dbFolder) return undefined;

          return dbFolder.name || 'Unknown';
        })
        // filter out falsy values (just in case the are unfound folders, which shouldn't be the case)
        .filter(Boolean) as string[];

      // path is constructed by joining folder names separated by `/` char
      return '/' + parentNames.join('/');
    },
    [foldersQuery.data]
  );

  return getItemPath;
};
